import React, {useEffect, useState} from "react";

import {DeleteOutlined, DownloadOutlined, EditOutlined, FolderAddOutlined, ReloadOutlined} from "@ant-design/icons";
import {Button, message, Statistic, Tooltip} from "antd";
import Search from "antd/es/input/Search";

import {CreateScope} from "./CreateScope";
import {ImportScopeModal} from "./ImportScopeModal";
import {ScopeRowActions} from "./ScopeRowActions";
import api from "../../api";
import axios from "../../api";
import * as Auth from "../../AuthService";
import {checkAnyOfPermissions, createScopeLink, downloadExcel, normalizeSearchQuery} from "../../helpers";
import {DataCard} from "../helpers/cards/Card";
import {StandardTable} from "../helpers/TablePanel";
import {ArchiveScopeModal} from "../helpers/modals/ArchiveModal"
import {Archive} from "../../variants"
import {extractErrorMessage} from "../../index";

export function ScopesList({projectId, activeKey, archived = false}) {
	const [isAdmin, setIsAdmin] = useState(false);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [startUpdate, setStartUpdate] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [createdByFilterOptions, setCreatedByFilterOptions] = useState([]);
	const [scopeTypeOptions, setScopeTypeOptions] = useState([]);
	const [selectedScope, setSelectedScope] = useState(null);
	const [isImportModalVisible, setIsImportModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [lastChangeByFilterOptions, setLastChangeByFilterOptions] = useState([]);
	const [isArchiveModalVisible, setArchiveModalVisible] = useState(false);

	const style = {
		height: "80vh",
		maxHeight: "80vh",
	};

	useEffect(() => {
		checkAnyOfPermissions(["senior-user", "approve-user", "insurance-admin", "admin", "material-analyst"]).then(
			() => setIsAdmin(true),
		);
		checkAnyOfPermissions(["superadmin"]).then(() => setIsSuperAdmin(true));

		getOptionsForFilter("user", setCreatedByFilterOptions);
		getOptionsForFilter("type", setScopeTypeOptions);
		getOptionsForFilter("last_change_by", setLastChangeByFilterOptions);
	}, [projectId]);

	const getOptionsForFilter = (fieldName, setFilterOptions) => {
		if (projectId) {
			api.get(`/projects/${projectId}/scopes/filter/options?field_name=${encodeURI(fieldName)}`).then(json =>
				setFilterOptions(json.data),
			);
		}
	};

	const columns = [
		{
			title: "Scope Name",
			dataIndex: "name",
			key: "name",
			render: (name, item) => <a href={createScopeLink(projectId, item._id)}>{name}</a>,
		},
		{
			title: "Scope Description",
			dataIndex: "description",
			key: "description",
		},
		{
			title: "Created By",
			dataIndex: "user",
			key: "user",
			filters: createdByFilterOptions.map(option => ({
				text: option,
				value: option,
			})),
		},
		Archive.enabled && {
			title: "Last change by",
			dataIndex: "last_change_by",
			key: "last_change_by",
			filters: lastChangeByFilterOptions.map(option => ({
				text: option,
				value: option,
			})),
		},
		{
			title: "Items in Scope",
			dataIndex: "quantity",
			key: "quantity",
			render: quantity => <Statistic value={quantity} valueStyle={{fontSize: 14}} />,
		},
		{
			title: "Actions",
			key: "actions",
			width: 180,
			render: (_, item) => {
				const hasPermission = item.type === "main-scope" ? isSuperAdmin : isAdmin;

				if (!hasPermission) return null;

				return (
					<ScopeRowActions>
						{!archived && (
							<Tooltip title="Edit">
								<span>
									<EditOutlined
										className={"beautiful-link"}
										onClick={() => editScope(item)}
										style={{fontSize: 18}}
									/>
								</span>
							</Tooltip>
						)}
						<Tooltip title="Export">
							<span>
								<DownloadOutlined
									className={"beautiful-link"}
									onClick={() => exportScope(item)}
									style={{fontSize: 18, cursor: "pointer"}}
								/>
							</span>
						</Tooltip>
						{Archive.enabled && !archived && (
							<Tooltip title="Archive">
								<span>
									<FolderAddOutlined
										className={"beautiful-link"}
										style={{fontSize: 18, cursor: "pointer"}}
										onClick={() => ArchiveScope(item)}
									/>
								</span>
							</Tooltip>
						)}
						{!archived && (
							<Tooltip title="Refresh">
								<span>
									<ReloadOutlined
										className={"beautiful-link"}
										style={{fontSize: 18, cursor: "pointer"}}
										onClick={() => refreshScope(item)}
									/>
								</span>
							</Tooltip>
						)}
						<Tooltip title="Delete">
							<span>
								<DeleteOutlined
									className={"beautiful-link"}
									onClick={() => deleteScope(item._id)}
									style={{fontSize: 18}}
								/>
							</span>
						</Tooltip>
					</ScopeRowActions>
				);
			},
		},
	].filter(Boolean);

	const deleteScope = _id => {
		api.get(`/projects/${projectId}/scopes/delete?id=${_id}`, {omitGlobalError: true})
			.then(() => {
				message.success(`The scope was successfully deleted`);
				setStartUpdate(!startUpdate);
			})
			.catch(async error => {
				console.log(error);
				const msg = await extractErrorMessage(error);
				message.error(msg);
			});
	};

	const editScope = scope => {
		setIsModalVisible(true);
		setSelectedScope(scope);
	};

	const exportScope = scope => {
		const hide = message.loading("Preparing Scope for download...", 0, () => {});
		const config = {...Auth.createConfig(), responseType: "blob"};
		axios
			.get(`/projects/${projectId}/scope/${scope._id}/export?archived=${archived}`, config)
			.then(json => downloadExcel(json.data, `Scope ${scope.name}.xlsx`))
			.catch(error => console.log(error))
			.finally(() => setTimeout(hide, 1));
	};

	const refreshScope = item => {
		api.post(`/projects/${projectId}/scopes/refresh?id=${item._id}`, {}, {omitGlobalError: true})
			.then(res => {
				const quantity = res.data.quantity ?? 0;
				const previousQuantity = res.data.previous_quantity ?? 0;
				const delta = quantity - previousQuantity;

				if (delta > 0) {
					void message.success(`The scope was refreshed. +${delta} material(s) added. New count: ${quantity}.`);
				} else if (delta < 0) {
					void message.success(`The scope was refreshed. ${Math.abs(delta)} material(s) removed. New count: ${quantity}.`);
				} else {
					void message.info(`The scope was refreshed. No changes — material count is still ${quantity}.`);
				}
				setStartUpdate(!startUpdate);
			})
			.catch(async error => {
				console.log(error);
				const msg = await extractErrorMessage(error);
				message.error(msg);
			});
	};

	const onFinish = () => {
		setIsImportModalVisible(false);
		setStartUpdate(!startUpdate);
		setLoading(false);
	};

	const ArchiveScope = scope => {
	    setArchiveModalVisible(true);
	    setSelectedScope(scope);
    };

    const onFinishArchive = (values, _id) => {
    	axios.post(
		    `/projects/${projectId}/scopes/archive?id=${_id}`, {
		    add_info: values?.add_info,
		}, {omitGlobalError: true})
        .then((json) => {
            message.success(`The scope was successfully archived`);
            setStartUpdate(!startUpdate);
            setArchiveModalVisible(false);
            setSelectedScope(null);
            ["Report_warning", "Scenario_warning"].forEach((key) => {
                const text = json?.data?.[key];
                if (text) {
                    message.warning(text);
                }
            });
        })
        .catch(async error => {
            console.log(error);
            const msg = await extractErrorMessage(error);
            message.error(msg);
        });
    };

	return (
		<DataCard style={style}>
			{isAdmin && (
				<div style={{paddingBottom: 10, float: "right"}}>
					<Button
						onClick={() => {
							setIsModalVisible(true);
						}}>
						Add new scope
					</Button>
				</div>
			)}
			{isAdmin && (
				<div style={{paddingBottom: 10, float: "right", marginRight: 10}}>
					<Button
						onClick={() => {
							setIsImportModalVisible(true);
						}}>
						Import scope
					</Button>
				</div>
			)}
			<ScopesTable
				columns={columns}
				projectId={projectId}
				isModalVisible={isModalVisible}
				activeKey={activeKey}
				update={startUpdate}
			/>

			<CreateScope
				isSuperAdmin={isSuperAdmin}
				isModalVisible={isModalVisible}
				scope={selectedScope}
				setSelectedScope={setSelectedScope}
				setIsModalVisible={value => {
					setSelectedScope(null);
					setIsModalVisible(value);
				}}
				projectId={projectId}
				archived={false}
			/>

			<ImportScopeModal
				isSuperAdmin={isSuperAdmin}
				isModalVisible={isImportModalVisible}
				setIsModalVisible={setIsImportModalVisible}
				onFinish={onFinish}
				projectId={projectId}
				spin={loading}
				setLoading={setLoading}
			/>

			{Archive.enabled && (
                <ArchiveScopeModal
                    isSuperAdmin={isSuperAdmin}
                    isModalVisible={isArchiveModalVisible}
                    scope={selectedScope}
                    setIsModalVisible={setArchiveModalVisible}
                    onFinish={onFinishArchive}
                    spin={loading}
                    setLoading={setLoading}
                />
            )}
		</DataCard>
	);
}

export function ScopesTable({columns, projectId, isModalVisible, activeKey, update}) {
	const [scopes, setScopes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [total, setTotal] = useState(0);
	const [filters, setFilters] = useState({});
	const [query, setQuery] = useState("");

	const getScopes = (_page = page, size = pageSize, _filters = filters, _query = query) => {
		setLoading(true);
		if (size !== pageSize || _filters !== filters || query !== _query) {
			_page = 1;
		}
		api.post(`/projects/${projectId}/scopes?page=${_page}&size=${size}`, {_filters, _query})
			.then(json => {
				setScopes(json.data.items);
				setTotal(json.data.total);
				setPage(_page);
				setQuery(_query);
				setFilters(_filters);
				setPageSize(size);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		if (projectId) {
			getScopes(1);
		}
	}, [projectId, isModalVisible, activeKey, update]);

	const onChange = (newPagination, _filters, _sorter) => {
		getScopes(newPagination.current, newPagination.pageSize, _filters);
	};

	return (
		<div>
			<div style={{marginBottom: 10}}>
				<Search enterButton="Search" onSearch={value => getScopes(page, pageSize, filters, normalizeSearchQuery(value))} />
			</div>
			{query && (
				<div style={{marginBottom: 5}}>
					Current Query: <b>{query}</b>
				</div>
			)}
			<StandardTable
				size={"small"}
				loading={loading}
				style={{width: "100%"}}
				total={total}
				currentPage={page}
				columns={columns}
				scroll={500}
				onChange={onChange}
				pageSize={pageSize}
				tableProps={{
					rowClassName: record => (record.type === "main-scope" ? "main-scope" : ""),
				}}
				data={scopes}
			/>
		</div>
	);
}
